"use client";

import { useState } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  MoreHorizontal,
  Send,
  ThumbsUp,
  Repeat2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { mockPosts, platformIcons, statusConfig } from "@/lib/mock-data";
import { useClient } from "@/lib/client-context";

function InstagramCard({ post }: { post: (typeof mockPosts)[0] }) {
  const status = statusConfig[post.status];
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-2.5">
          <div className="h-7 w-7 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 p-[1.5px]">
            <div className="h-full w-full rounded-full bg-card flex items-center justify-center">
              <span className="text-[8px] font-bold">SS</span>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold leading-tight">socialstandards</p>
            <p className="text-[10px] text-muted-foreground leading-tight">Sponsored</p>
          </div>
        </div>
        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="aspect-square bg-gradient-to-br from-pink-100 via-purple-50 to-blue-100 flex items-center justify-center overflow-hidden">
        {post.mediaUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={post.mediaUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <p className="text-xs text-muted-foreground font-medium">Image Preview</p>
        )}
      </div>
      <div className="px-3 pt-2.5 pb-1">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-3">
            <Heart className="h-5 w-5 cursor-pointer hover:text-red-500 transition-colors" />
            <MessageCircle className="h-5 w-5 cursor-pointer" />
            <Send className="h-5 w-5 cursor-pointer" />
          </div>
          <Bookmark className="h-5 w-5 cursor-pointer" />
        </div>
        {post.engagement && (
          <p className="text-xs font-semibold mb-1">{post.engagement.likes.toLocaleString()} likes</p>
        )}
        <p className="text-xs leading-relaxed line-clamp-3">
          <span className="font-semibold mr-1">socialstandards</span>
          {post.content}
        </p>
        {post.engagement && (
          <p className="text-[10px] text-muted-foreground mt-1">
            View all {post.engagement.comments} comments
          </p>
        )}
        <p className="text-[10px] text-muted-foreground uppercase tracking-wide mt-1 pb-2">
          {format(post.scheduledAt, "MMMM d, yyyy")}
        </p>
      </div>
      <div className="px-3 py-2 border-t bg-muted/30 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className={cn("h-1.5 w-1.5 rounded-full", status.dot)} />
          <span className={cn("text-[10px] font-medium", status.color)}>{status.label}</span>
        </div>
        <span className="text-[10px] text-muted-foreground">{format(post.scheduledAt, "MMM d")}</span>
      </div>
    </Card>
  );
}

function TwitterCard({ post }: { post: (typeof mockPosts)[0] }) {
  const status = statusConfig[post.status];
  return (
    <Card className="overflow-hidden">
      <div className="p-3">
        <div className="flex gap-2.5">
          <Avatar className="h-8 w-8 shrink-0">
            <AvatarFallback className="bg-slate-900 text-white text-[9px] font-bold">SS</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-xs font-bold">Social Standards</span>
              <span className="text-[11px] text-muted-foreground">@socialstandards</span>
              <span className="text-[11px] text-muted-foreground">&middot; {format(post.scheduledAt, "MMM d")}</span>
            </div>
            <p className="text-[13px] mt-1 leading-relaxed">{post.content}</p>
            <div className="flex items-center justify-between mt-2.5 text-muted-foreground max-w-[250px]">
              <button className="flex items-center gap-1 text-[11px] hover:text-blue-500 transition-colors">
                <MessageCircle className="h-3.5 w-3.5" />
                {post.engagement?.comments || 12}
              </button>
              <button className="flex items-center gap-1 text-[11px] hover:text-green-500 transition-colors">
                <Repeat2 className="h-3.5 w-3.5" />
                {post.engagement?.shares || 8}
              </button>
              <button className="flex items-center gap-1 text-[11px] hover:text-red-500 transition-colors">
                <Heart className="h-3.5 w-3.5" />
                {post.engagement?.likes || 47}
              </button>
              <button className="flex items-center gap-1 text-[11px] hover:text-blue-500 transition-colors">
                <Bookmark className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="px-3 py-2 border-t bg-muted/30 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className={cn("h-1.5 w-1.5 rounded-full", status.dot)} />
          <span className={cn("text-[10px] font-medium", status.color)}>{status.label}</span>
        </div>
        <span className="text-[10px] text-muted-foreground">{format(post.scheduledAt, "MMM d")}</span>
      </div>
    </Card>
  );
}

function LinkedInCard({ post }: { post: (typeof mockPosts)[0] }) {
  const status = statusConfig[post.status];
  return (
    <Card className="overflow-hidden">
      <div className="p-3">
        <div className="flex items-center gap-2.5 mb-2.5">
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-blue-700 text-white text-xs font-bold">SS</AvatarFallback>
          </Avatar>
          <div>
            <p className="text-xs font-semibold">Social Standards</p>
            <p className="text-[10px] text-muted-foreground">12,543 followers</p>
            <p className="text-[10px] text-muted-foreground">{format(post.scheduledAt, "MMM d")} &middot; 🌐</p>
          </div>
        </div>
        <p className="text-xs leading-relaxed whitespace-pre-line line-clamp-5">{post.content}</p>
        {post.engagement && (
          <div className="flex items-center justify-between text-[10px] text-muted-foreground py-2 mt-2 border-b">
            <span>👍 ❤️ {post.engagement.likes.toLocaleString()}</span>
            <span>{post.engagement.comments} comments</span>
          </div>
        )}
        <div className="flex items-center justify-around pt-1.5 text-muted-foreground">
          <button className="flex items-center gap-1.5 text-[11px] font-medium py-1.5 hover:bg-muted rounded-md px-2.5 transition-colors">
            <ThumbsUp className="h-3.5 w-3.5" /> Like
          </button>
          <button className="flex items-center gap-1.5 text-[11px] font-medium py-1.5 hover:bg-muted rounded-md px-2.5 transition-colors">
            <MessageCircle className="h-3.5 w-3.5" /> Comment
          </button>
          <button className="flex items-center gap-1.5 text-[11px] font-medium py-1.5 hover:bg-muted rounded-md px-2.5 transition-colors">
            <Share2 className="h-3.5 w-3.5" /> Share
          </button>
        </div>
      </div>
      <div className="px-3 py-2 border-t bg-muted/30 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className={cn("h-1.5 w-1.5 rounded-full", status.dot)} />
          <span className={cn("text-[10px] font-medium", status.color)}>{status.label}</span>
        </div>
        <span className="text-[10px] text-muted-foreground">{format(post.scheduledAt, "MMM d")}</span>
      </div>
    </Card>
  );
}

function FacebookCard({ post }: { post: (typeof mockPosts)[0] }) {
  const status = statusConfig[post.status];
  return (
    <Card className="overflow-hidden">
      <div className="p-3">
        <div className="flex items-center gap-2.5 mb-2.5">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-blue-600 text-white text-xs font-bold">SS</AvatarFallback>
          </Avatar>
          <div>
            <p className="text-xs font-semibold">Social Standards</p>
            <p className="text-[10px] text-muted-foreground">
              {format(post.scheduledAt, "MMM d 'at' h:mm a")} &middot; 🌍
            </p>
          </div>
          <MoreHorizontal className="h-4 w-4 text-muted-foreground ml-auto" />
        </div>
        <p className="text-xs leading-relaxed whitespace-pre-line line-clamp-5 mb-2">{post.content}</p>
        <div className="aspect-video bg-gradient-to-br from-blue-50 to-blue-100 rounded-md flex items-center justify-center mb-2 overflow-hidden">
          {post.mediaUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={post.mediaUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <p className="text-[10px] text-muted-foreground font-medium">Link Preview</p>
          )}
        </div>
        {post.engagement && (
          <div className="flex items-center justify-between text-[10px] text-muted-foreground py-1.5 border-b">
            <span>👍 {post.engagement.likes.toLocaleString()}</span>
            <span>{post.engagement.comments} comments &middot; {post.engagement.shares} shares</span>
          </div>
        )}
        <div className="flex items-center justify-around pt-1.5 text-muted-foreground">
          <button className="flex items-center gap-1.5 text-[11px] font-medium py-1.5 hover:bg-muted rounded-md px-3 transition-colors">
            <ThumbsUp className="h-3.5 w-3.5" /> Like
          </button>
          <button className="flex items-center gap-1.5 text-[11px] font-medium py-1.5 hover:bg-muted rounded-md px-3 transition-colors">
            <MessageCircle className="h-3.5 w-3.5" /> Comment
          </button>
          <button className="flex items-center gap-1.5 text-[11px] font-medium py-1.5 hover:bg-muted rounded-md px-3 transition-colors">
            <Share2 className="h-3.5 w-3.5" /> Share
          </button>
        </div>
      </div>
      <div className="px-3 py-2 border-t bg-muted/30 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className={cn("h-1.5 w-1.5 rounded-full", status.dot)} />
          <span className={cn("text-[10px] font-medium", status.color)}>{status.label}</span>
        </div>
        <span className="text-[10px] text-muted-foreground">{format(post.scheduledAt, "MMM d")}</span>
      </div>
    </Card>
  );
}

function PlatformColumn({
  platform,
  posts,
}: {
  platform: string;
  posts: (typeof mockPosts);
}) {
  const info = platformIcons[platform];
  return (
    <div className="flex flex-col min-w-0">
      {/* Column header */}
      <div className="flex items-center gap-2.5 mb-4 px-1">
        <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", info.bg)}>
          <span className={cn("text-xs font-bold", info.color)}>
            {info.label.charAt(0)}
          </span>
        </div>
        <div>
          <p className="text-sm font-semibold">{info.label}</p>
          <p className="text-[11px] text-muted-foreground">{posts.length} posts</p>
        </div>
      </div>

      {/* Cards */}
      <div className="space-y-4">
        {posts.map((post) => {
          if (platform === "INSTAGRAM") return <InstagramCard key={post.id} post={post} />;
          if (platform === "TWITTER") return <TwitterCard key={post.id} post={post} />;
          if (platform === "LINKEDIN") return <LinkedInCard key={post.id} post={post} />;
          return <FacebookCard key={post.id} post={post} />;
        })}
      </div>
    </div>
  );
}

export default function FeedPage() {
  const { selectedClientId } = useClient();
  const [view, setView] = useState<"columns" | "single">("columns");
  const [platformFilter, setPlatformFilter] = useState("all");

  const filteredPosts = mockPosts.filter(
    (p) =>
      (selectedClientId === "all" || p.clientId === selectedClientId) &&
      (platformFilter === "all" || p.platform === platformFilter)
  );

  // Group posts by platform
  const grouped = filteredPosts.reduce<Record<string, typeof mockPosts>>(
    (acc, post) => {
      if (!acc[post.platform]) acc[post.platform] = [];
      acc[post.platform].push(post);
      return acc;
    },
    {}
  );

  const platformOrder = ["INSTAGRAM", "TWITTER", "LINKEDIN", "FACEBOOK"];
  const activePlatforms = platformOrder.filter((p) => grouped[p]?.length);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-8 py-5 border-b bg-card">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Feed View</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Preview how your posts will look on each platform
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Tabs value={view} onValueChange={(v) => setView(v as "columns" | "single")}>
            <TabsList className="h-9">
              <TabsTrigger value="columns" className="text-xs px-3">Columns</TabsTrigger>
              <TabsTrigger value="single" className="text-xs px-3">Single</TabsTrigger>
            </TabsList>
          </Tabs>
          <Select value={platformFilter} onValueChange={(v) => v && setPlatformFilter(v)}>
            <SelectTrigger className="w-[150px] h-9">
              <SelectValue placeholder="Platform" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Platforms</SelectItem>
              <SelectItem value="INSTAGRAM">Instagram</SelectItem>
              <SelectItem value="FACEBOOK">Facebook</SelectItem>
              <SelectItem value="TWITTER">Twitter/X</SelectItem>
              <SelectItem value="LINKEDIN">LinkedIn</SelectItem>
            </SelectContent>
          </Select>
          <Button className="shadow-sm shadow-primary/25">
            <Plus className="h-4 w-4 mr-2" />
            New Post
          </Button>
        </div>
      </div>

      <div className="flex-1 p-6 overflow-auto">
        {view === "columns" ? (
          <div
            className={cn(
              "grid gap-6",
              activePlatforms.length === 1 && "grid-cols-1 max-w-md mx-auto",
              activePlatforms.length === 2 && "grid-cols-2",
              activePlatforms.length === 3 && "grid-cols-3",
              activePlatforms.length >= 4 && "grid-cols-4"
            )}
          >
            {activePlatforms.map((platform) => (
              <PlatformColumn
                key={platform}
                platform={platform}
                posts={grouped[platform]}
              />
            ))}
          </div>
        ) : (
          <div className="max-w-[440px] mx-auto space-y-4">
            {filteredPosts.map((post) => {
              if (post.platform === "INSTAGRAM") return <InstagramCard key={post.id} post={post} />;
              if (post.platform === "TWITTER") return <TwitterCard key={post.id} post={post} />;
              if (post.platform === "LINKEDIN") return <LinkedInCard key={post.id} post={post} />;
              return <FacebookCard key={post.id} post={post} />;
            })}
          </div>
        )}
      </div>
    </div>
  );
}
